/**
 * DroneStatusPanel — Left-side vertical panel showing the health and status
 * of every drone in the player's swarm.
 */

import { DRONE_ACTIVE_ABILITIES, DroneType } from '../../shared/types.js';
import { MAX_DRONES_PER_PLAYER } from '../../shared/constants.js';
import { getDroneSpriteVisual } from '../rendering/droneSpriteAssets.js';

const DRONE_TYPE_NAMES: Record<number, string> = {
  0: 'STR',
  1: 'SNP',
  2: 'HLR',
  3: 'INT',
  4: 'SUP',
  5: 'GRD',
};

const TIER_CSS = ['common', 'rare', 'legend'] as const;
const MAX_DRONE_ROWS = MAX_DRONES_PER_PLAYER;
const ABILITY_HOTKEYS = ['U', 'I', 'O', 'J', 'K', 'L'] as const;

export interface DroneStatusEntry {
  droneId: string;
  droneType: number;
  tier: number;
  healthFraction: number;
  alive: boolean;
  abilityCooldown: number;
  activeCardSlot?: number;
  activeCardType?: number;
}

interface DroneRowCache {
  row: HTMLElement;
  checkbox: HTMLElement;
  sprite: HTMLImageElement;
  badge: HTMLElement;
  dot: HTMLElement;
  bar: HTMLElement;
  cardBadge: HTMLElement;
  leaderBadge: HTMLElement;
  abilityBtn: HTMLButtonElement;
  dismissBtn: HTMLButtonElement;
  // Hash of the last rendered visible state. Subsequent update() calls
  // skip the row when the hash matches — this is what makes per-drone
  // changes (alive, hp) visible across same-array-reference updates.
  lastHash: string;
}

export class DroneStatusPanel {
  private container: HTMLElement;
  private titleEl: HTMLElement;
  private listEl: HTMLElement;
  private rowCache: DroneRowCache[] = [];
  private selectCb: ((droneId: string, additive: boolean) => void) | null = null;
  private leaderCb: ((droneId: string) => void) | null = null;
  private selectedIds: Set<string> = new Set();
  private leaderId: string = '';
  private abilityCb: ((droneId: string) => void) | null = null;
  private dismissCb: ((droneId: string) => void) | null = null;

  constructor(parentElement: HTMLElement) {
    this.container = document.createElement('div');
    this.container.className = 'hud-panel drone-status hidden';

    this.titleEl = document.createElement('div');
    this.titleEl.className = 'hud-panel-title';
    this.titleEl.textContent = `SWARM [0/${MAX_DRONE_ROWS}]`;
    this.container.appendChild(this.titleEl);

    this.listEl = document.createElement('div');
    this.container.appendChild(this.listEl);

    // Pre-create row elements for reuse
    for (let i = 0; i < MAX_DRONE_ROWS; i++) {
      const row = document.createElement('div');
      row.className = 'drone-entry';
      row.style.display = 'none';
      row.style.cursor = 'pointer';

      row.addEventListener('click', (ev: MouseEvent) => {
        const droneId = row.dataset.droneId;
        if (!droneId) return;
        const additive = ev.shiftKey || ev.metaKey || ev.ctrlKey;
        this.selectCb?.(droneId, additive);
      });
      row.addEventListener('dblclick', () => {
        const droneId = row.dataset.droneId;
        if (droneId) this.leaderCb?.(droneId);
      });

      // Checkbox indicator — makes it obvious that rows are "checkable" for
      // multi-drone orbit commands. Purely visual; clicks still go through the
      // row handler above, which handles shift/cmd/ctrl-click correctly.
      const checkbox = document.createElement('div');
      checkbox.className = 'drone-checkbox';
      checkbox.addEventListener('click', (ev: MouseEvent) => {
        ev.stopPropagation();
        const droneId = row.dataset.droneId;
        if (droneId) this.selectCb?.(droneId, true);
      });
      row.appendChild(checkbox);

      const sprite = document.createElement('img');
      sprite.className = 'drone-sprite-thumb';
      sprite.alt = '';
      sprite.draggable = false;
      row.appendChild(sprite);

      const badge = document.createElement('div');
      badge.className = 'drone-type-badge';
      row.appendChild(badge);

      const dot = document.createElement('div');
      dot.className = 'drone-tier-dot common';
      row.appendChild(dot);

      const barBg = document.createElement('div');
      barBg.className = 'drone-health-bar-bg';

      const bar = document.createElement('div');
      bar.className = 'drone-health-bar';
      barBg.appendChild(bar);
      row.appendChild(barBg);

      const leaderBadge = document.createElement('div');
      leaderBadge.className = 'drone-leader-badge';
      leaderBadge.textContent = '\u2605';
      leaderBadge.style.display = 'none';
      row.appendChild(leaderBadge);

      const cardBadge = document.createElement('div');
      cardBadge.className = 'drone-card-badge';
      cardBadge.style.display = 'none';
      row.appendChild(cardBadge);

      const abilityBtn = document.createElement('button');
      abilityBtn.className = 'drone-ability-btn';
      abilityBtn.style.display = 'none';
      row.appendChild(abilityBtn);

      const dismissBtn = document.createElement('button');
      dismissBtn.className = 'drone-dismiss-btn';
      dismissBtn.textContent = 'x';
      dismissBtn.title = 'Dismiss drone';
      dismissBtn.addEventListener('click', (ev: MouseEvent) => {
        ev.stopPropagation();
        const droneId = row.dataset.droneId;
        if (droneId) this.dismissCb?.(droneId);
      });
      row.appendChild(dismissBtn);

      this.listEl.appendChild(row);
      this.rowCache.push({
        row,
        checkbox,
        sprite,
        badge,
        dot,
        bar,
        cardBadge,
        leaderBadge,
        abilityBtn,
        dismissBtn,
        lastHash: '',
      });
    }

    parentElement.appendChild(this.container);
  }

  /** Update cached rows from current state. */
  update(drones: DroneStatusEntry[]): void {
    const aliveCount = drones.filter((d) => d.alive).length;
    this.titleEl.textContent = `SWARM [${aliveCount}/${drones.length}]`;

    for (let i = 0; i < MAX_DRONE_ROWS; i++) {
      const cache = this.rowCache[i];
      if (i < drones.length) {
        const d = drones[i];
        const isSelected = this.selectedIds.has(d.droneId);
        const isLeader = d.droneId === this.leaderId;
        const hash = this.rowHash(d, i, isSelected, isLeader);
        if (hash === cache.lastHash) continue;
        cache.lastHash = hash;
        this.renderRow(cache, d, i, isSelected, isLeader);
      } else if (cache.lastHash !== '') {
        cache.row.style.display = 'none';
        cache.lastHash = '';
      }
    }
  }

  /**
   * Stable hash of one row's visible state. Health is quantized to 2
   * decimals (matches bar precision) and cooldown to whole seconds
   * (matches button text) so sub-pixel/sub-second jitter doesn't churn
   * the DOM.
   */
  private rowHash(d: DroneStatusEntry, slotIndex: number, isSelected: boolean, isLeader: boolean): string {
    const frac = Math.max(0, Math.min(1, d.healthFraction)).toFixed(2);
    const cooldownSec = Math.max(0, Math.ceil(d.abilityCooldown / 1000));
    const slot = d.activeCardSlot ?? 255;
    return [
      d.droneId,
      d.alive ? '1' : '0',
      frac,
      d.droneType,
      d.tier,
      slot,
      cooldownSec,
      isSelected ? '1' : '0',
      isLeader ? '1' : '0',
      slotIndex,
    ].join('|');
  }

  /** Re-render one row from its current state (called only on hash mismatch). */
  private renderRow(cache: DroneRowCache, d: DroneStatusEntry, slotIndex: number, isSelected: boolean, isLeader: boolean): void {
    cache.row.style.display = '';
    cache.row.dataset.droneId = d.droneId;

    cache.row.className = 'drone-entry' + (d.alive ? '' : ' dead') + (isSelected ? ' selected' : '') + (isLeader ? ' leader' : '');
    cache.checkbox.className = 'drone-checkbox' + (isSelected ? ' checked' : '');
    cache.checkbox.textContent = isSelected ? '\u2611' : '\u2610';
    cache.leaderBadge.style.display = isLeader ? '' : 'none';

    cache.sprite.src = getDroneSpriteVisual(d.droneType).url;
    cache.badge.textContent = DRONE_TYPE_NAMES[d.droneType] ?? '???';

    const tierClass = TIER_CSS[d.tier] ?? 'common';
    cache.dot.className = `drone-tier-dot ${tierClass}`;

    const frac = Math.max(0, Math.min(1, d.healthFraction));
    cache.bar.style.width = `${frac * 100}%`;
    cache.bar.style.backgroundColor = this.healthColor(frac);

    const activeSlot = d.activeCardSlot ?? 255;
    const hasActiveCard = activeSlot !== 255 && activeSlot >= 0;
    cache.cardBadge.style.display = hasActiveCard ? '' : 'none';
    cache.cardBadge.textContent = hasActiveCard ? String(activeSlot + 1) : '';
    cache.cardBadge.title = hasActiveCard ? `Card ${activeSlot + 1}` : '';

    const abilityDef = DRONE_ACTIVE_ABILITIES[d.droneType as DroneType];
    if (abilityDef && d.alive) {
      const hotkey = ABILITY_HOTKEYS[slotIndex] ?? '';
      cache.abilityBtn.style.display = '';
      cache.abilityBtn.title = hotkey
        ? `${hotkey} - ${abilityDef.name}`
        : abilityDef.name;
      if (d.abilityCooldown > 0) {
        cache.abilityBtn.textContent = hotkey
          ? `${hotkey} ${Math.ceil(d.abilityCooldown / 1000)}s`
          : `${Math.ceil(d.abilityCooldown / 1000)}s`;
        cache.abilityBtn.disabled = true;
        cache.abilityBtn.classList.add('on-cooldown');
      } else {
        cache.abilityBtn.textContent = hotkey
          ? `${hotkey} ${abilityDef.name}`
          : abilityDef.name;
        cache.abilityBtn.disabled = false;
        cache.abilityBtn.classList.remove('on-cooldown');
      }
      cache.abilityBtn.onclick = () => {
        if (d.abilityCooldown <= 0) this.abilityCb?.(d.droneId);
      };
    } else {
      cache.abilityBtn.style.display = 'none';
    }
  }

  onDroneSelect(cb: (droneId: string, additive: boolean) => void): void { this.selectCb = cb; }
  onSetLeader(cb: (droneId: string) => void): void { this.leaderCb = cb; }
  onTriggerAbility(cb: (droneId: string) => void): void { this.abilityCb = cb; }
  onDismissDrone(cb: (droneId: string) => void): void { this.dismissCb = cb; }

  /**
   * Update selection state. Accepts a list of selected ids (for multi-select).
   * The third arg is kept for backward-compat with single-id callers; the
   * leader id is unchanged.
   */
  setSelection(selectedDroneIds: string[] | string, leaderDroneId: string): void {
    this.selectedIds = new Set(Array.isArray(selectedDroneIds)
      ? selectedDroneIds
      : selectedDroneIds
        ? [selectedDroneIds]
        : []);
    this.leaderId = leaderDroneId;
  }

  show(): void {
    this.container.classList.remove('hidden');
  }

  hide(): void {
    this.container.classList.add('hidden');
  }

  /** Map 0-1 health fraction to a green -> yellow -> red gradient. */
  private healthColor(frac: number): string {
    if (frac > 0.6) return '#00ff88'; // green
    if (frac > 0.3) return '#ffcc00'; // yellow
    return '#ff4444'; // red
  }
}
